import { useState, useEffect } from 'react';
import { Save, Loader2, ExternalLink, AlertTriangle, CheckCircle, Copy, RefreshCw, Eye, EyeOff, Key, Trash2 } from 'lucide-react';
import { adminAPI, PlanConfig } from '../../lib/api';
import { toast } from 'react-hot-toast';

interface SecretsStatus {
  secret_key_configured: boolean;
  webhook_secret_configured: boolean;
  secret_key_source: 'environment' | 'database' | 'none';
  webhook_secret_source: 'environment' | 'database' | 'none';
  api_working: boolean;
}

export default function AdminStripePage() {
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<'unknown' | 'configured' | 'not_configured'>('unknown');
  const [secretsStatus, setSecretsStatus] = useState<SecretsStatus | null>(null);

  // Secret input states
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [isSavingSecret, setIsSavingSecret] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansResponse, statusResponse] = await Promise.all([
        adminAPI.listPlans(),
        adminAPI.getStripeStatus(),
      ]);

      setPlans(plansResponse.plans);
      setSecretsStatus(statusResponse.status);

      // Check if Stripe is configured by looking for price IDs
      const hasStripeConfig = plansResponse.plans.some(
        p => p.stripe_price_id_monthly || p.stripe_price_id_yearly
      );
      setStripeStatus(hasStripeConfig ? 'configured' : 'not_configured');
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSecret = async (secretName: string, value: string) => {
    if (!value.trim()) {
      toast.error('Please enter a value');
      return;
    }

    setIsSavingSecret(secretName);
    try {
      await adminAPI.setSecret(secretName, value.trim());
      toast.success(`${secretName} saved successfully`);

      // Clear the input
      if (secretName === 'STRIPE_SECRET_KEY') {
        setStripeSecretKey('');
      } else {
        setWebhookSecret('');
      }

      // Reload status
      loadData();
    } catch (error) {
      console.error(`Failed to save ${secretName}:`, error);
      toast.error(`Failed to save ${secretName}`);
    } finally {
      setIsSavingSecret(null);
    }
  };

  const handleDeleteSecret = async (secretName: string) => {
    if (!confirm(`Are you sure you want to delete ${secretName}? This will disable Stripe integration until you re-configure it.`)) {
      return;
    }

    setIsSavingSecret(secretName);
    try {
      await adminAPI.deleteSecret(secretName);
      toast.success(`${secretName} deleted`);
      loadData();
    } catch (error) {
      console.error(`Failed to delete ${secretName}:`, error);
      toast.error(`Failed to delete ${secretName}`);
    } finally {
      setIsSavingSecret(null);
    }
  };

  const handleUpdatePlan = async (planId: string, updates: Partial<PlanConfig>) => {
    setIsSaving(planId);
    try {
      await adminAPI.updatePlan(planId, updates);
      toast.success('Plan updated successfully');
      loadData();
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast.error('Failed to update plan');
    } finally {
      setIsSaving(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-800">Stripe Settings</h1>
        <p className="mt-1 text-graphite">Configure Stripe products and pricing for each plan</p>
      </div>

      {/* API Secrets Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Key className="h-5 w-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-navy-800">Stripe API Secrets</h2>
          {secretsStatus?.api_working && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Connected</span>
          )}
        </div>

        <div className="space-y-6">
          {/* Stripe Secret Key */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-graphite">
                Stripe Secret Key
                <span className="text-gray-400 font-normal ml-2">(starts with sk_live_ or sk_test_)</span>
              </label>
              {secretsStatus?.secret_key_configured && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  secretsStatus.secret_key_source === 'database'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {secretsStatus.secret_key_source === 'database' ? 'Stored in Database' : 'Set via CLI'}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  value={stripeSecretKey}
                  onChange={(e) => setStripeSecretKey(e.target.value)}
                  placeholder={secretsStatus?.secret_key_configured ? '••••••••••••••••' : 'sk_live_...'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button
                onClick={() => handleSaveSecret('STRIPE_SECRET_KEY', stripeSecretKey)}
                disabled={!stripeSecretKey || isSavingSecret === 'STRIPE_SECRET_KEY'}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isSavingSecret === 'STRIPE_SECRET_KEY' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </button>
              {secretsStatus?.secret_key_configured && secretsStatus?.secret_key_source === 'database' && (
                <button
                  onClick={() => handleDeleteSecret('STRIPE_SECRET_KEY')}
                  disabled={isSavingSecret === 'STRIPE_SECRET_KEY'}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Get your secret key from{' '}
              <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                Stripe Dashboard → Developers → API keys
              </a>
            </p>
          </div>

          {/* Webhook Secret */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-graphite">
                Webhook Signing Secret
                <span className="text-gray-400 font-normal ml-2">(starts with whsec_)</span>
              </label>
              {secretsStatus?.webhook_secret_configured && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  secretsStatus.webhook_secret_source === 'database'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {secretsStatus.webhook_secret_source === 'database' ? 'Stored in Database' : 'Set via CLI'}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={secretsStatus?.webhook_secret_configured ? '••••••••••••••••' : 'whsec_...'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button
                onClick={() => handleSaveSecret('STRIPE_WEBHOOK_SECRET', webhookSecret)}
                disabled={!webhookSecret || isSavingSecret === 'STRIPE_WEBHOOK_SECRET'}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isSavingSecret === 'STRIPE_WEBHOOK_SECRET' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </button>
              {secretsStatus?.webhook_secret_configured && secretsStatus?.webhook_secret_source === 'database' && (
                <button
                  onClick={() => handleDeleteSecret('STRIPE_WEBHOOK_SECRET')}
                  disabled={isSavingSecret === 'STRIPE_WEBHOOK_SECRET'}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Create a webhook at{' '}
              <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                Stripe Dashboard → Developers → Webhooks
              </a>
              , then copy the signing secret
            </p>
          </div>

          {/* Status Summary */}
          <div className={`rounded-lg p-4 ${
            secretsStatus?.api_working
              ? 'bg-green-50 border border-green-200'
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start gap-3">
              {secretsStatus?.api_working ? (
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              )}
              <div>
                <h4 className={`font-medium ${secretsStatus?.api_working ? 'text-green-800' : 'text-yellow-800'}`}>
                  {secretsStatus?.api_working ? 'Stripe API Connected' : 'Configuration Incomplete'}
                </h4>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    {secretsStatus?.secret_key_configured ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className={secretsStatus?.secret_key_configured ? 'text-green-700' : 'text-yellow-700'}>
                      Secret Key: {secretsStatus?.secret_key_configured ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {secretsStatus?.webhook_secret_configured ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className={secretsStatus?.webhook_secret_configured ? 'text-green-700' : 'text-yellow-700'}>
                      Webhook Secret: {secretsStatus?.webhook_secret_configured ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {secretsStatus?.api_working ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className={secretsStatus?.api_working ? 'text-green-700' : 'text-yellow-700'}>
                      API Connection: {secretsStatus?.api_working ? 'Working' : 'Not tested'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price IDs Status */}
      <div className={`rounded-xl border p-4 ${
        stripeStatus === 'configured'
          ? 'bg-green-50 border-green-200'
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-start gap-3">
          {stripeStatus === 'configured' ? (
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          )}
          <div>
            <h3 className={`font-medium ${
              stripeStatus === 'configured' ? 'text-green-800' : 'text-yellow-800'
            }`}>
              {stripeStatus === 'configured'
                ? 'Stripe Price IDs configured'
                : 'Stripe Price IDs required'}
            </h3>
            <p className={`text-sm mt-1 ${
              stripeStatus === 'configured' ? 'text-green-700' : 'text-yellow-700'
            }`}>
              {stripeStatus === 'configured'
                ? 'Your Stripe price IDs are configured. Users can upgrade their plans.'
                : 'Add your Stripe price IDs below to enable paid subscriptions.'}
            </p>
          </div>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-800 mb-4">Setup Instructions</h2>
        <ol className="space-y-4 text-sm text-graphite">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-semibold">1</span>
            <div>
              <p className="font-medium text-navy-800">Create Products in Stripe</p>
              <p className="mt-1">Go to your Stripe Dashboard → Products → Add Product. Create one product for each plan (Pro, Enterprise).</p>
              <a
                href="https://dashboard.stripe.com/products"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-primary-500 hover:text-primary-600"
              >
                Open Stripe Products <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-semibold">2</span>
            <div>
              <p className="font-medium text-navy-800">Add Prices to Each Product</p>
              <p className="mt-1">For each product, add two recurring prices: Monthly and Yearly. Make sure to use the prices shown below.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-semibold">3</span>
            <div>
              <p className="font-medium text-navy-800">Copy Price IDs</p>
              <p className="mt-1">After creating prices, copy each Price ID (starts with <code className="bg-gray-100 px-1 rounded">price_</code>) and paste it below.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-semibold">4</span>
            <div>
              <p className="font-medium text-navy-800">Set Up Webhook</p>
              <p className="mt-1">Create a webhook endpoint in Stripe pointing to:</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">https://api.chatbotembed.com/v1/webhooks/stripe</code>
                <button
                  onClick={() => copyToClipboard('https://api.chatbotembed.com/v1/webhooks/stripe')}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Select events: checkout.session.completed, customer.subscription.*, invoice.*
              </p>
              <a
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-primary-500 hover:text-primary-600"
              >
                Open Stripe Webhooks <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </li>
        </ol>
      </div>

      {/* Plan Configuration */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-navy-800">Plan Pricing & Stripe IDs</h2>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {plans.map((plan) => (
          <PlanStripeCard
            key={plan.id}
            plan={plan}
            onUpdate={(updates) => handleUpdatePlan(plan.id, updates)}
            isSaving={isSaving === plan.id}
          />
        ))}
      </div>

    </div>
  );
}

interface PlanStripeCardProps {
  plan: PlanConfig;
  onUpdate: (updates: Partial<PlanConfig>) => void;
  isSaving: boolean;
}

function PlanStripeCard({ plan, onUpdate, isSaving }: PlanStripeCardProps) {
  const [monthlyPriceId, setMonthlyPriceId] = useState(plan.stripe_price_id_monthly || '');
  const [yearlyPriceId, setYearlyPriceId] = useState(plan.stripe_price_id_yearly || '');
  const [priceMonthly, setPriceMonthly] = useState(plan.price_monthly);
  const [priceYearly, setPriceYearly] = useState(plan.price_yearly);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const changed =
      monthlyPriceId !== (plan.stripe_price_id_monthly || '') ||
      yearlyPriceId !== (plan.stripe_price_id_yearly || '') ||
      priceMonthly !== plan.price_monthly ||
      priceYearly !== plan.price_yearly;
    setHasChanges(changed);
  }, [monthlyPriceId, yearlyPriceId, priceMonthly, priceYearly, plan]);

  const handleSave = () => {
    onUpdate({
      stripe_price_id_monthly: monthlyPriceId || null,
      stripe_price_id_yearly: yearlyPriceId || null,
      price_monthly: priceMonthly,
      price_yearly: priceYearly,
    });
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const isFree = plan.price_monthly === 0;

  return (
    <div className={`bg-white rounded-xl shadow-sm border-2 p-6 ${
      plan.plan_name === 'pro' ? 'border-primary-200' : 'border-gray-200'
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold text-navy-800">{plan.display_name}</h3>
            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
              {plan.plan_name}
            </span>
            {plan.plan_name === 'pro' && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                Popular
              </span>
            )}
          </div>
          <p className="text-sm text-graphite mt-1">{plan.description}</p>
        </div>
        {!isFree && hasChanges && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Changes
          </button>
        )}
      </div>

      {isFree ? (
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            This is the free tier. No Stripe configuration needed.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Monthly */}
          <div className="space-y-4">
            <h4 className="font-medium text-navy-800 flex items-center gap-2">
              Monthly Billing
              {monthlyPriceId && <CheckCircle className="h-4 w-4 text-green-500" />}
            </h4>

            <div>
              <label className="block text-sm text-graphite mb-1">Price (in cents)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={priceMonthly}
                  onChange={(e) => setPriceMonthly(parseInt(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="2900"
                />
                <span className="text-sm text-gray-500 w-20">
                  = {formatPrice(priceMonthly)}/mo
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-graphite mb-1">Stripe Price ID</label>
              <input
                type="text"
                value={monthlyPriceId}
                onChange={(e) => setMonthlyPriceId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                placeholder="price_1234567890abcdef"
              />
              <p className="text-xs text-gray-500 mt-1">
                Create a recurring monthly price in Stripe and paste the ID here
              </p>
            </div>
          </div>

          {/* Yearly */}
          <div className="space-y-4">
            <h4 className="font-medium text-navy-800 flex items-center gap-2">
              Yearly Billing
              {yearlyPriceId && <CheckCircle className="h-4 w-4 text-green-500" />}
              <span className="text-xs text-green-600 font-normal">Save ~17%</span>
            </h4>

            <div>
              <label className="block text-sm text-graphite mb-1">Price (in cents)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={priceYearly}
                  onChange={(e) => setPriceYearly(parseInt(e.target.value) || 0)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="29000"
                />
                <span className="text-sm text-gray-500 w-20">
                  = {formatPrice(priceYearly)}/yr
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm text-graphite mb-1">Stripe Price ID</label>
              <input
                type="text"
                value={yearlyPriceId}
                onChange={(e) => setYearlyPriceId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                placeholder="price_1234567890abcdef"
              />
              <p className="text-xs text-gray-500 mt-1">
                Create a recurring yearly price in Stripe and paste the ID here
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Plan Limits Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-graphite mb-2">Plan Limits</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="text-gray-600">
            <strong>{plan.max_widgets}</strong> widgets
          </span>
          <span className="text-gray-600">
            <strong>{plan.max_domains_per_widget}</strong> domains/widget
          </span>
          <span className="text-gray-600">
            <strong>{plan.max_messages_per_month.toLocaleString()}</strong> messages/mo
          </span>
          <span className="text-gray-600">
            <strong>{plan.max_api_keys}</strong> API keys
          </span>
        </div>
      </div>
    </div>
  );
}
